import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ROUTES, WORKSPACES, type NavEntry, type NavItem, type NavSection } from "@/app/constants";
import { useNavAccess } from "@/features/access";
import { ChevronDown, ChevronsLeft, ChevronsRight, LayoutDashboard } from "bizpandit-ui";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const LAST_WORKSPACE_KEY = "bp.lastWorkspace";

function workspaceForPath(pathname: string): string | null {
  if (pathname.startsWith("/astrology")) return "astrology";
  if (pathname.startsWith("/pre-sales")) return "presales";
  if (pathname.startsWith("/post-sales")) return "postsales";
  if (pathname.startsWith("/hr")) return "hr";
  // Home belongs to no workspace: it is the person's page, one click from
  // anywhere (owner, 2026-09-15). The rail keeps showing whichever workspace
  // they were in, so landing on Home never empties the menu.
  if (pathname === ROUTES.home) return null;
  return "admin";
}

function useActiveWorkspace() {
  const { pathname } = useLocation();
  const resolved = workspaceForPath(pathname);
  useEffect(() => {
    if (resolved) { try { sessionStorage.setItem(LAST_WORKSPACE_KEY, resolved); } catch { /* private mode */ } }
  }, [resolved]);
  if (resolved) return resolved;
  try {
    const last = sessionStorage.getItem(LAST_WORKSPACE_KEY);
    return last && WORKSPACES.some((w) => w.id === last) ? last : "presales";
  } catch { return "presales"; }
}

/** The first leaf a filtered nav still offers — the workspace's real entry point for this person. */
function firstPermittedRoute(entries: NavEntry[]): string | null {
  for (const e of entries) {
    if (e.kind === "item") return e.href;
    if (e.items[0]) return e.items[0].href;
  }
  return null;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const activeWorkspaceId = useActiveWorkspace();
  const navigate = useNavigate();
  const workspace = WORKSPACES.find((w) => w.id === activeWorkspaceId) ?? WORKSPACES[0]!;

  // RBAC v2: hide what the user has no grant for. Entries with no mapped slug
  // are kept — see useNavAccess for why this one place fails open.
  const { filterEntries } = useNavAccess();
  const navEntries = filterEntries(workspace.nav);

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex flex-col bg-navy text-white transition-all duration-300",
        collapsed ? "w-16" : "w-[200px]",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-white/10 px-4">
        <span className="shrink-0 text-2xl text-rose">◉</span>
        {!collapsed && (
          <span className="truncate text-lg font-semibold tracking-[0.18em] text-rose">
            BIZPANDIT
          </span>
        )}
      </div>

      {/* Dashboard — the one dashboard (/home), outside every workspace, one click away */}
      <div className={cn("border-b border-white/10", collapsed ? "px-1 py-2" : "px-2 py-2")}>
        <NavLeafRow item={{ label: "Dashboard", href: ROUTES.home, icon: LayoutDashboard }} collapsed={collapsed} />
      </div>

      {/* Workspace switcher — a workspace with no screen this person may open is
          not offered at all (the Astrology workspace is the platform owner's
          only, 2026-09-17; a second tenant's rail would be empty). */}
      <div className={cn("border-b border-white/10", collapsed ? "px-1 py-2" : "px-2 py-2")}>
        {WORKSPACES.filter((ws) => filterEntries(ws.nav).length > 0).map((ws) => {
          const isActive = ws.id === activeWorkspaceId;
          return (
            <button
              key={ws.id}
              type="button"
              title={ws.label}
              // Land on the first screen this person may open in the workspace,
              // not on a fixed home that may 403 for them: a Sales Executive
              // clicking HR was sent to Staff Directory and read "needs the
              // employee:read permission" (QA v2 §1, 2026-09-17).
              onClick={() => navigate(firstPermittedRoute(filterEntries(ws.nav)) ?? ws.homeRoute)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors",
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/50 hover:bg-white/10 hover:text-white/80",
              )}
            >
              <ws.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{ws.label}</span>}
            </button>
          );
        })}
      </div>

      {/* Nav items for current workspace */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
        {navEntries.map((entry, i) =>
          entry.kind === "item" ? (
            <NavLeafRow key={entry.href} item={entry} collapsed={collapsed} />
          ) : (
            <NavSectionRow
              key={`sec-${i}`}
              section={entry}
              collapsed={collapsed}
            />
          ),
        )}
      </nav>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 border-t border-white/10 px-4 py-3 text-sm font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white"
      >
        {collapsed ? (
          <ChevronsRight className="h-4 w-4" />
        ) : (
          <ChevronsLeft className="h-4 w-4" />
        )}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}

/* ---------------------------------------------------------------- */
/* Row for a flat nav item                                          */
/* ---------------------------------------------------------------- */
function NavLeafRow({
  item,
  collapsed,
  nested,
}: {
  item: NavItem;
  collapsed: boolean;
  nested?: boolean;
}) {
  return (
    <NavLink
      to={item.href}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          nested && !collapsed && "pl-9",
          isActive
            ? "bg-rose text-roseink"
            : "text-white/70 hover:bg-white/10 hover:text-white",
        )
      }
    >
      <item.icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

/* ---------------------------------------------------------------- */
/* Collapsible section (e.g. "Admin")                               */
/* ---------------------------------------------------------------- */
function NavSectionRow({
  section,
  collapsed,
}: {
  section: NavSection;
  collapsed: boolean;
}) {
  const location = useLocation();
  const containsActive = useMemo(
    () => section.items.some((it) => location.pathname.startsWith(it.href)),
    [location.pathname, section.items],
  );
  const [open, setOpen] = useState<boolean>(containsActive);

  // When sidebar is collapsed, render children as tooltipped icons directly.
  if (collapsed) {
    return (
      <div className="space-y-1">
        <div
          className="flex items-center justify-center rounded-xl px-3 py-2 text-white/40"
          title={section.label}
        >
          <section.icon className="h-5 w-5" />
        </div>
        {section.items.map((it) => (
          <NavLeafRow key={it.href} item={it} collapsed />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors",
          containsActive
            ? "text-white"
            : "text-white/60 hover:bg-white/10 hover:text-white",
        )}
      >
        <section.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 truncate text-left">{section.label}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform",
            open ? "rotate-0" : "-rotate-90",
          )}
        />
      </button>
      {open &&
        section.items.map((it) => (
          <NavLeafRow key={it.href} item={it} collapsed={collapsed} nested />
        ))}
    </div>
  );
}
